import { useMemo, useState } from 'react';
import Countdown from '../components/Countdown';
import Banner from '../components/Banner';
import { OneBallotResourceType } from '../resources/Ballot/One';
import ObjectiveStateResource from '../resources/ObjectiveState';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import BallotsScoresResource from '../resources/Ballot/Scores';
import optionTypes from '../resources/Ballot/constants/optionTypes';
import { listify, makeClassName, pluralize } from '@siteless/util';
import useCopy from '../util/useCopy';
import { ListIcon } from './ListIcon';
import { ObjectiveStateResultsModal } from './ObjectiveStateResultsModal';
import { WrapBanner } from './WrapBanner';
import ObjectiveStateSlimResource from 'src/resources/ObjectiveState/ObjectiveStateSlim';
import { thisOscarsYear } from 'src/config/constants';
import GlobalPhaseResource, { GlobalPhase } from '../resources/GlobalPhase';
import SnailIcon from './SnailIcon';

// TODO: remove after 2026 — Live Action Short tie override
let LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES = new Set([
  'iTZ1eyNVdCrdPfyR2fpoh', // The Singers
  'rLEc89MBBOh2EGXj-dKwG', // Two People Exchanging Saliva
]);
import SnailStrikeThroughIcon from './SnailStrikeThroughIcon';
import PopcornIcon from './PopcornIcon';
import CountdownWithDate from './CountdownWithDate';

let bannerTabClass =
  '-mt-[22px] -mb-[4px] flex items-center rounded-b-lg px-2.5 pb-[5px] pt-[9px]';

export let PersistentBanner = ({
  ballot,
  onOpenDelayModal,
  delayMs,
  isSpectator,
  onOpenSpectatorInfo,
  winner: winnerProp,
}: {
  ballot: OneBallotResourceType;
  onOpenDelayModal?: () => void;
  delayMs?: number;
  isSpectator?: boolean;
  onOpenSpectatorInfo?: () => void;
  winner?: { handles: string[]; points: number };
}) => {
  let copy = useCopy();

  let [allUserProfiles] = AllUserProfilesResource.useLoadable();
  let ballotScores = BallotsScoresResource.use();
  let objectiveState = ObjectiveStateResource.use();
  let globalPhase = GlobalPhaseResource.use();
  let nextYearObjectiveStateSlim = ObjectiveStateSlimResource.use(
    thisOscarsYear + 1,
  );
  let [showResultsModal, setShowResultsModal] = useState(false);

  let spectatorFooter = isSpectator ? (
    <span
      className="flex cursor-pointer items-center gap-1"
      onClick={(e) => {
        e.stopPropagation();
        onOpenSpectatorInfo?.();
      }}
    >
      <PopcornIcon className="h-3.5 w-3.5" />
      <span className="underline decoration-dotted">{copy.spectating}</span>
    </span>
  ) : undefined;
  let showDelayDiscovery =
    (delayMs === undefined || delayMs === null) && onOpenDelayModal;

  let resultsModal = (
    <ObjectiveStateResultsModal
      isOpen={showResultsModal}
      onClose={() => {
        setShowResultsModal(false);
      }}
    />
  );

  let globalWinner = useMemo(() => {
    let scoredBallots = Object.entries(ballotScores)
      .map(([createdByUserId, { score }]) => ({
        score,
        handle: allUserProfiles?.users[createdByUserId]?.handle,
      }))
      .filter(({ handle }) => handle)
      .sort((a, b) => b.score - a.score);

    if (!scoredBallots[0]) return;

    let highScore = scoredBallots[0].score;

    return {
      handles: scoredBallots
        .filter(({ score }) => score === highScore)
        .map(({ handle }) => handle)
        .filter((handle) => typeof handle === 'string') as string[],
      points: highScore,
    };
  }, [allUserProfiles, ballotScores]);

  let winner = winnerProp || globalWinner;

  if (
    globalPhase === GlobalPhase.NEXT_YEAR_PREP &&
    nextYearObjectiveStateSlim?.ceremonyDetails?.nominationsAnnouncementAt
  )
    return (
      <Banner
        key={'nextYear'} // needed to get banner to respond to body size change
        label={copy.nominationsAreAnnounced}
        body={
          <CountdownWithDate
            timestampSeconds={
              nextYearObjectiveStateSlim.ceremonyDetails
                .nominationsAnnouncementAt.seconds
            }
          />
        }
      />
    );

  if (objectiveState?.didCeremonyEnd && winner)
    return (
      <>
        {resultsModal}
        <Banner
          key={'winner'} // needed to get banner to respond to body size change
          label={
            <span className="inline-flex w-full max-w-full items-center">
              <span className="min-w-0 flex-1 truncate">
                {`${pluralize({
                  hideCount: true,
                  count: winner.handles.length,
                  singular: copy.winnerIs,
                  plural: copy.winnersAre,
                })}...`}
              </span>
              <span
                className={makeClassName(
                  bannerTabClass,
                  'ml-1.5 cursor-pointer bg-rose-bright/10',
                )}
                onClick={() => setShowResultsModal(true)}
              >
                <ListIcon className="h-4 w-4" />
              </span>
            </span>
          }
          body={`${listify(winner.handles, copy.and)} ${copy._with} ${pluralize(
            {
              count: winner.points,
              singular: copy.point,
              plural: copy.points,
            },
          )}!`}
          footer={spectatorFooter}
        />
        <WrapBanner
          // Give users a sec after we switch to the winner screen
          delayShowMs={8000}
        />
      </>
    );

  if (objectiveState.didCeremonyStart) {
    let lastResult =
      objectiveState.results[objectiveState?.results.length - 1] || {};

    let lastResultCategoryKey = Object.keys(lastResult)[0];
    let lastResultFilmKey = Object.values(lastResult)[0] as string | undefined;

    let lastResultCategory =
      objectiveState.nomineeCategories[
        // artificially allow indexing with string
        lastResultCategoryKey as keyof typeof objectiveState.nomineeCategories
      ];

    let lastResultWinner = (lastResultCategory && lastResultFilmKey
      ? lastResultCategory.nominees[lastResultFilmKey] || null
      : null) as unknown as {
      id: string;
      detail?: string;
      filmId: string;
      peopleIds: string[];
    };

    let mostRecentResultPick = (() => {
      for (let [sectionKey, { options }] of Object.entries(
        objectiveState.ballotShape,
      )) {
        let optionKey = ballot?.[sectionKey];
        if (!optionKey) continue;
        let option = options[optionKey];

        if (!('categoryKey' in option.data)) continue;
        if (option.data.categoryKey !== lastResultCategoryKey) continue;

        if (option.type === optionTypes.pass.key)
          return {
            optionKey,
            option,
            optionComponentProps: optionTypes.pass.getOptionComponentProps({
              copy,
              objectiveState,
              data: {
                // @ts-expect-error
                categoryKey: option.data
                  .categoryKey as keyof typeof objectiveState.nomineeCategories,
                points: option.data.points,
              },
            }),
          };

        if (option.type === optionTypes.winner.key)
          return {
            optionKey,

            option,
            optionComponentProps: optionTypes.winner.getOptionComponentProps({
              copy,
              objectiveState,
              data: {
                // @ts-expect-error
                categoryKey: option.data
                  .categoryKey as keyof typeof objectiveState.nomineeCategories,
                nomineeId: option.data.nomineeId,
                points: option.data.points,
              },
            }),
          };
      }
    })();

    let hasAnnouncements = objectiveState.results.length > 0;
    let hasWinners = objectiveState.results.some((result) =>
      Object.values(result).some((v) => v),
    );

    let bannerLabelText = hasAnnouncements
      ? lastResultCategory?.title || copy.nextCategory
      : `Fantasy Oscars ${thisOscarsYear}`;
    let bannerBody = lastResultWinner
      ? (() => {
          let props = optionTypes.winner.getOptionComponentProps({
            copy,
            objectiveState,
            data: {
              // @ts-expect-error artificially allow indexing with string
              categoryKey:
                lastResultCategory.key as keyof typeof objectiveState.nomineeCategories,
              nomineeId: lastResultWinner.id,
              points: 0, // DUMMY
            },
          });
          return (
            <>
              {props.title}
              {props.details && (
                <span className="text-purple-500"> {props.details}</span>
              )}
            </>
          );
        })()
      : hasAnnouncements
      ? lastResultCategory?.title || copy.toBeAnnounced
      : copy.firstCategoryToBeAnnounced;
    let bannerSubtitle = lastResultWinner
      ? mostRecentResultPick?.option.type === optionTypes.winner.key &&
        // TODO: remove after 2026 — Live Action Short tie override
        (mostRecentResultPick?.option.data.nomineeId === lastResultWinner.id ||
          (LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES.has(
            mostRecentResultPick?.option.data.nomineeId,
          ) &&
            LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES.has(lastResultWinner.id)))
        ? '🎉  + ' +
          mostRecentResultPick.option.data.points +
          ' ' +
          copy.points +
          '  🎉'
        : undefined
      : mostRecentResultPick
      ? copy['Your pick'] +
        ': ' +
        mostRecentResultPick.optionComponentProps?.title
      : undefined;

    return (
      <>
        {resultsModal}
        <Banner
          key={'results'}
          label={
            <span className="inline-flex w-full max-w-full items-center">
              <span className="min-w-0 flex-1 truncate">
                {lastResultWinner
                  ? bannerLabelText
                  : hasAnnouncements
                  ? copy.nextCategory
                  : bannerLabelText}
              </span>
              {onOpenDelayModal &&
                (showDelayDiscovery ? (
                  <span
                    className={makeClassName(
                      bannerTabClass,
                      'ml-2 box-content cursor-pointer gap-1 border border-dashed border-t-0 border-rose-default text-rose-default',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDelayModal();
                    }}
                  >
                    <SnailIcon className="h-4 w-4" />
                    <span className="text-xs font-medium normal-case">
                      {copy.tapMe}
                    </span>
                  </span>
                ) : (
                  <span
                    className={makeClassName(
                      bannerTabClass,
                      'ml-2',
                      delayMs
                        ? 'bg-rose-bright/10'
                        : 'box-content border-l border-r border-b border-neutral-0 text-neutral-300',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDelayModal();
                    }}
                  >
                    <span className="flex items-center gap-1">
                      {delayMs ? (
                        <SnailIcon className="h-4 w-4" />
                      ) : (
                        <SnailStrikeThroughIcon className="h-4 w-4 -mx-1" />
                      )}
                      {Boolean(delayMs) && (
                        <span className="text-xs font-medium normal-case">
                          {Math.round((delayMs || 0) / 1000)}s
                        </span>
                      )}
                    </span>
                  </span>
                ))}
              {hasWinners && (
                <span
                  className={makeClassName(
                    bannerTabClass,
                    'ml-1.5 cursor-pointer bg-rose-bright/10',
                  )}
                  onClick={() => setShowResultsModal(true)}
                >
                  <ListIcon className="h-4 w-4" />
                </span>
              )}
            </span>
          }
          body={bannerBody}
          subtitle={bannerSubtitle}
          footer={spectatorFooter}
        />
      </>
    );
  }

  return (
    <Banner
      key={'ceremonyBefore'} // needed to get banner to respond to body size change
      label={copy.theOscarsAreLive}
      body={
        objectiveState?.ceremonyStartAt && (
          <CountdownWithDate
            timestampSeconds={objectiveState.ceremonyStartAt.seconds}
          />
        )
      }
    />
  );
};
